import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { basePageWrap } from '../BasePage';
import BaseContext from '../../containers/BasePage/BaseContext';
import { CookieStateContext } from '../BasePage/state';
import isEmpty from '../../utils/isEmpty';
import StreamField from '../../components/StreamField';
import Breadcrumbs from '../../components/Breadcrumbs';
import ShareFeedbackSegment from '../../components/ShareFeedbackSegment';
// import ShortFacts from '../../components/ShortFacts';
import ArticleList, { GeneralArticleList, MoreInArticleList } from '../../components/ArticleList';
import Hero from '../../components/Hero';
import LinkList from '../../components/LinkList';
import BackBanner from '../../components/BackBanner';
import BookmarkButton from '../../components/BookmarkButton';
import MapExpander from '../../components/MapExpander';
import Search from '../../components/Search';
import Sponsors from '../../components/Sponsors';
import ArticleDate from '../../components/ArticleDate';
import styles from './LandingPage.module.scss';

const LandingPage = ({
    breadcrumbs = {},
    title = '',
    preamble = '',
    // shortFacts,
    contentBlocks = [],
    moduleBlocks = [],
    linksDocumentsBlock = [],
    sections = {
        sections: [],
    },
    sponsored = {},
    searchSettings = {},
    landingType = '',
    isCityLanding = false,
    hero = {},
    date = {},
    map,
    backToMap = false,
    shareFeedback = {},
}) => {
    const { isStorybook } = useContext(BaseContext);
    const cookieContext = useContext(CookieStateContext);
    const cookieConsent = cookieContext?.state?.categories && cookieContext.state.categories.includes('functionality');

    let articleLists = sections.sections.map((section, i) => {
        return <ArticleList key={i} isLandingPage={true} {...section} />;
    });

    if (sections.general && !isEmpty(sections.general.items)) {
        articleLists = [<GeneralArticleList key="general" {...sections.general} />, ...articleLists];
    }

    if (sections.moreIn && !isEmpty(sections.moreIn.items)) {
        articleLists = [...articleLists, <MoreInArticleList key="morein" {...sections.moreIn} parentTitle={title} />];
    }

    hero = {
        ...hero,
        isBelowBreadcrumbs: breadcrumbs && breadcrumbs.items && breadcrumbs.items.length > 1,
    };
    // Check if there's already a Map component in contentBlocks
    const hasMapComponent =
        Array.isArray(contentBlocks) && contentBlocks.some((block) => block.type === 'map' || block.type === 'Map');

    const hasMapExpander = map && !isEmpty(map.coords) && !hasMapComponent;
    const showBookmark = (cookieConsent || isStorybook) && shareFeedback?.showBookmark;

    const classes = classNames(styles['LandingPage'], {
        [styles['LandingPage--HasMap']]: map && !isEmpty(map.coords),
    });

    return (
        <div className={classes}>
            <Breadcrumbs {...breadcrumbs} />
            <Hero {...hero} hideContent={true} />
            <div className={styles['LandingPage__Main']} id="main-content">
                <div className={styles['LandingPage__Container']}>
                    <h1 className={styles['LandingPage__Title']}>{title}</h1>
                    {preamble && (
                        <div
                            className={styles['LandingPage__Preamble']}
                            dangerouslySetInnerHTML={{ __html: preamble }}
                        />
                    )}
                    {!isEmpty(date) && <ArticleDate {...date} />}
                </div>

                <div className={styles['LandingPage__Actions']}>
                    {showBookmark && (
                        <BookmarkButton
                            untranslatedModifiers={['BookmarkButton--WithMapExpanderHeight']}
                            untranslatedButtonModifiers={hasMapExpander ? ['Button--WithMapExpanderHeight'] : []}
                            {...shareFeedback}
                        />
                    )}
                    {hasMapExpander && (
                        <MapExpander
                            googleMapsApiKey={map.googleMapsApiKey}
                            items={map.coords}
                            zoomLevel={map.zoomLevel}
                        />
                    )}
                </div>

                {linksDocumentsBlock && linksDocumentsBlock.length > 0 && (
                    <div className={styles['LandingPage__Links'] + ' ' + styles['LandingPage__Links--Desktop']}>
                        <LinkList {...linksDocumentsBlock[0].value} />
                    </div>
                )}
            </div>

            {Array.isArray(contentBlocks) && (
                <StreamField items={contentBlocks} pageType="Landing" globalMapMeta={map} />
            )}

            {backToMap && <BackBanner backToMap={true} />}

            {linksDocumentsBlock && linksDocumentsBlock.length > 0 && (
                <div className={styles['LandingPage__Container']}>
                    <div className={styles['LandingPage__Links'] + ' ' + styles['LandingPage__Links--Mobile']}>
                        <LinkList {...linksDocumentsBlock[0].value} />
                    </div>
                </div>
            )}

            {landingType !== 'where-to-go' && (
                <Search searchSettings={searchSettings} embeddedByContainer="LandingPage">
                    {!isEmpty(articleLists) && articleLists}
                </Search>
            )}
            {landingType === 'where-to-go' && !isEmpty(articleLists) && articleLists}

            {Array.isArray(moduleBlocks) && (
                <StreamField items={moduleBlocks} pageType="Landing" globalMapMeta={map} id="modules" />
            )}

            {!isEmpty(shareFeedback) && <ShareFeedbackSegment {...shareFeedback} />}

            {!isEmpty(sponsored) && <Sponsors {...sponsored} />}
        </div>
    );
};

LandingPage.propTypes = {
    title: PropTypes.string.isRequired,
    preamble: PropTypes.string.isRequired,
    breadcrumbs: PropTypes.object,
    shortFacts: PropTypes.object,
    searchSettings: PropTypes.object,
    contentBlocks: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
    sponsored: PropTypes.object,
    linksDocumentsBlock: PropTypes.array,
    sections: PropTypes.object,
    landingType: PropTypes.string,
    isCityLanding: PropTypes.bool,
    hero: PropTypes.object,
    date: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
    map: PropTypes.object,
    backToMap: PropTypes.bool,
    shareFeedback: PropTypes.object,
};

export default basePageWrap(LandingPage);
